import { AccountsService, TeamRole } from 'base/accounts_service';
import { AnalyticsService } from 'base/analytics';
import { Asset } from 'base/asset';
import { Avatar } from 'base/ui/avatar';
import { Badge } from 'base/ui/badge';
import { Banner } from 'base/ui/banner';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import {
  formatError,
  InvisibleForm,
  RedErrorText,
  useSubmit,
} from 'base/ui/form';
import { IconAdd, IconOption, IconPng } from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { LoadingDots } from 'base/ui/loading_dots';
import { MenuButton } from 'base/ui/menu';
import { menuItem, MenuModel } from 'base/ui/menu_model';
import { DividerLine } from 'base/ui/panel';
import { SelectButton } from 'base/ui/select';
import { TickRow } from 'base/ui/tick_row';
import { ToastQueue } from 'base/ui/toast';
import {
  TextRegular,
  TextRegularLight,
  TextSmall,
  TextSmallLight,
  TextTinyLight,
  Title1,
  Title6,
} from 'base/ui/typography';
import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import { TeamFeatureGate } from 'blocks/billing/feature_gate_dialog';
import { currentUrl } from 'blocks/current_url';
import {
  manageAccountRouteToUrl,
  maybeParseManageAccountRoute,
} from 'blocks/manage_account/routes';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { InviteTeamForm } from './invite_team_form';
import styles from './manage_team.css';
import { InviteModel, ManageTeamModel, MemberModel } from './manage_team_model';
import { TwoColumn, useAvatarUpload, useLogoUpload } from './ui';
import upsellCardImage from './upsell-graphic.file.svg';

export function ManageTeam({
  accountsService,
  toastQueue,
  analytics,
}: {
  accountsService: AccountsService;
  toastQueue: ToastQueue;
  analytics: AnalyticsService;
}) {
  const [error, setError] = React.useState<unknown>();
  const [model, setModel] = React.useState<ManageTeamModel | undefined>();

  React.useEffect(() => {
    accountsService
      .getTeam()
      .then((res) => setModel(new ManageTeamModel(res, accountsService)))
      .catch(setError);
  }, [accountsService]);

  if (!model) {
    return (
      <Rows spacing={32}>
        <Title1>Your Team</Title1>
        {error ? (
          <RedErrorText error={error} />
        ) : (
          <LoadingDots aspectRatio={2 / 1} />
        )}
      </Rows>
    );
  }
  const showMembers =
    !model.needsToUpgrade || model.members.some((m) => !m.isMe);
  return (
    <Rows spacing={32}>
      <Title1>Your Team</Title1>
      {model.needsToUpgrade && (
        <TeamFeatureGate fullWidth bordered analytics={analytics} />
      )}
      {showMembers && model.myRole === 'admin' ? (
        <>
          <TeamDetailsForm
            model={model}
            accountsService={accountsService}
            toastQueue={toastQueue}
          />
          <DividerLine />
        </>
      ) : null}
      {showMembers && <TeamMembers model={model} toastQueue={toastQueue} />}
    </Rows>
  );
}

export function TeamUpsellCard() {
  return (
    <div className={styles.upsellCard}>
      <div className={styles.upsellCardText}>
        <Rows spacing={32} xalign="start">
          <Rows spacing={24} xalign="start">
            <Rows spacing={16} xalign="start">
              <Title6>Supercharge your design process with your team</Title6>
              <TextSmallLight>
                Invite team members to collaborate on Site Builder projects and
                streamline your design process.
              </TextSmallLight>
            </Rows>
            <Rows spacing={8} xalign="start">
              <TickRow size="small">Real-time collaboration</TickRow>
              <TickRow size="small">Shared team projects</TickRow>
              <TickRow size="small">Centralized billing</TickRow>
            </Rows>
          </Rows>
          <Button
            variant="primary"
            label="Upgrade to Pro"
            href={authRouteToUrl({
              path: 'upgrade',
              context: { redirect: getCurrentLocationForRedirect() },
            })}
          />
        </Rows>
      </div>
      <img className={styles.upsellCardImage} src={upsellCardImage} />
    </div>
  );
}

const TeamDetailsForm = observer(function TeamDetailsForm({
  model,
  toastQueue,
  accountsService,
}: {
  model: ManageTeamModel;
  toastQueue: ToastQueue;
  accountsService: AccountsService;
}) {
  const [name, setName] = React.useState(model.name);

  const [loading, error, handleSubmit] = useSubmit(async () => {
    await model.changeName(name);
    toastQueue.push({ label: 'Team name updated' });
  });

  const edited = name !== model.name;
  return (
    <InvisibleForm onSubmit={handleSubmit} loading={loading}>
      <Rows spacing={24}>
        <Inline spacing={8}>
          <Title6>Team Details</Title6>
          <SpacerGrow />
          <Button
            type="submit"
            variant="primary"
            label="Save"
            loading={loading}
            disabled={!edited}
            tooltip={!edited ? 'No changes to save' : undefined}
          />
        </Inline>
        <TwoColumn>
          <AvatarField
            teamName={name}
            accountsService={accountsService}
            toastQueue={toastQueue}
            color={model.color}
            currentAvatar={model.avatar}
          />
          <LogoField
            accountsService={accountsService}
            toastQueue={toastQueue}
            currentLogo={model.logo}
          />
        </TwoColumn>
        <TwoColumn>
          <Rows spacing={8}>
            <TextSmall color="grey">Team Name</TextSmall>
            <FormInput
              required
              size="large"
              type="text"
              autoComplete="team-name"
              value={name}
              onChange={setName}
            />
          </Rows>
          <div />
        </TwoColumn>
        <RedErrorText error={error} />
      </Rows>
    </InvisibleForm>
  );
});

/** Show a loading toast, but only if it takes longer than 500ms */
function showLoadingToast(toastQueue: ToastQueue, label: string): () => void {
  let removeToast: (() => void) | undefined;
  const timeout = setTimeout(() => {
    removeToast = toastQueue.addPersistent(() => ({ label }));
  }, 500);
  return () => {
    clearTimeout(timeout);
    removeToast?.();
  };
}

function AvatarField({
  teamName,
  color,
  accountsService,
  toastQueue,
  currentAvatar,
}: {
  teamName: string;
  color: string;
  accountsService: AccountsService;
  toastQueue: ToastQueue;
  currentAvatar?: Asset;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selectFile = React.useCallback(() => {
    inputRef.current?.click();
  }, []);
  const { avatar, loading, chooseAvatar, deleteAvatar } = useAvatarUpload({
    upload: (file: File) => accountsService.uploadTeamAvatar(file),
    remove: () => accountsService.deleteTeamAvatar(),
    toastQueue,
    currentAvatar,
  });
  const [first, second] = teamName.split(' ');
  return (
    <Rows spacing={16} xalign="start">
      <div className={styles.avatarBox}>
        <Avatar
          size="xlarge"
          shape="square"
          avatar={avatar}
          firstName={first}
          lastName={second}
          color={color}
          onClick={selectFile}
        />
      </div>
      <Rows spacing={8} xalign="start">
        <TextRegular>Team Icon</TextRegular>
        <ImageNoteBox text="Replace the team icon in the dashboard and the favicon with your own brand" />
        <Inline spacing={16}>
          <input
            ref={inputRef}
            type="file"
            className={styles.hidden}
            tabIndex={-1}
            aria-hidden="true"
            accept="image/jpeg,image/png,image/webp"
            onChange={chooseAvatar}
          />
          <Button
            onClick={selectFile}
            loading={loading}
            type="button"
            icon={IconPng}
            label={avatar ? 'Upload new icon' : 'Upload icon'}
          />
          {avatar && !loading && (
            <Button
              variant="select"
              onClick={deleteAvatar}
              loading={loading}
              type="button"
              label="Delete"
            />
          )}
        </Inline>
        <TextTinyLight color="grey">
          PNG or JPG at least 160px in height
        </TextTinyLight>
      </Rows>
    </Rows>
  );
}

function LogoField({
  accountsService,
  toastQueue,
  currentLogo,
}: {
  accountsService: AccountsService;
  toastQueue: ToastQueue;
  currentLogo?: Asset;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selectFile = React.useCallback(() => {
    inputRef.current?.click();
  }, []);
  const { avatar, loading, chooseLogo, deleteLogo } = useLogoUpload({
    upload: (file: File) => accountsService.uploadTeamLogo(file),
    remove: () => accountsService.deleteTeamLogo(),
    toastQueue,
    currentLogo,
  });
  return (
    <Rows spacing={16} xalign="start">
      {avatar ? (
        <div className={styles.logoBox}>
          <img src={avatar.files[0].url} className={styles.teamLogo} />
        </div>
      ) : (
        <div className={styles.emptyLogoBox} />
      )}
      <Rows spacing={8} xalign="start">
        <TextRegular>Company logo</TextRegular>
        <ImageNoteBox text="Replace the Relume logo with your own brand when sharing" />
        <Inline spacing={16}>
          <input
            ref={inputRef}
            type="file"
            className={styles.hidden}
            tabIndex={-1}
            aria-hidden="true"
            accept="image/png,image/svg+xml"
            onChange={chooseLogo}
          />
          <Button
            onClick={selectFile}
            loading={loading}
            type="button"
            icon={IconPng}
            label={avatar ? 'Upload new logo' : 'Upload logo'}
          />
          {avatar && !loading && (
            <Button
              variant="select"
              onClick={deleteLogo}
              loading={loading}
              type="button"
              label="Delete"
            />
          )}
        </Inline>
        <TextTinyLight color="grey">
          Transparent SVG or PNG at least 20px height
        </TextTinyLight>
      </Rows>
    </Rows>
  );
}

const TeamMembers = observer(function _TeamMembers({
  model,
  toastQueue,
}: {
  model: ManageTeamModel;
  toastQueue: ToastQueue;
}) {
  const route = maybeParseManageAccountRoute(currentUrl);
  const inviteOpen =
    (route?.path === 'manage-account/team' && route.showInviteNewUsers) ??
    false;
  const setInviteOpen = (open: boolean) =>
    window.history.replaceState(
      {},
      '',
      manageAccountRouteToUrl({
        path: 'manage-account/team',
        showInviteNewUsers: open,
      }),
    );
  const [removing, setRemoving] = React.useState<MemberModel | undefined>();

  async function resendInvite(invite: InviteModel) {
    const done = showLoadingToast(
      toastQueue,
      `Resending invite to ${invite.email}...`,
    );
    try {
      await invite.resend();
      toastQueue.push({ label: `Resent invite to ${invite.email}` });
    } catch (error) {
      toastQueue.push({ label: formatError(error) });
    } finally {
      done();
    }
  }

  async function revokeInvite(invite: InviteModel) {
    const done = showLoadingToast(
      toastQueue,
      `Revoking invite from ${invite.email}...`,
    );
    try {
      await model.revokeInvite(invite);
      toastQueue.push({ label: `Revoked invite to ${invite.email}` });
    } catch (error) {
      toastQueue.push({ label: formatError(error) });
    } finally {
      done();
    }
  }

  return (
    <Rows spacing={24}>
      <Inline spacing={8}>
        <Title6>
          {model.myRole === 'admin'
            ? 'Team Members'
            : model.name || 'Untitled Team'}
        </Title6>
        <SpacerGrow />
        {model.canInvite && (
          <Button
            variant="primary"
            icon={IconAdd}
            label="Invite Members"
            onClick={() => setInviteOpen(true)}
          />
        )}
      </Inline>
      {model.myRole === 'admin' && <VacantSeatsBanner model={model} />}
      {inviteOpen && (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          model={model}
          toastQueue={toastQueue}
        />
      )}
      <Rows spacing={0}>
        {model.members.map((m) => (
          <MemberOrInviteRow
            key={m.id}
            model={m}
            action={<ChangeRoleDropdown model={model} subject={m} />}
            menu={
              model.myRole === 'admin' || m.isMe
                ? [
                    menuItem({
                      label: m.isMe ? 'Leave team' : 'Remove from team',
                      onClick: () => setRemoving(m),
                      disabled:
                        m.role === 'admin' &&
                        model.members.filter((m) => m.role === 'admin')
                          .length === 1,
                      disabledReason: 'You cannot remove the last admin',
                    }),
                  ]
                : []
            }
          />
        ))}
        {model.invites.map((i) => (
          <MemberOrInviteRow
            key={i.email}
            model={i}
            action={
              <>
                <InviteSentBadge />
                <ChangeRoleDropdown model={model} subject={i} />
              </>
            }
            menu={
              model.myRole === 'admin'
                ? [
                    menuItem({
                      label: 'Resend invite',
                      onClick: () => resendInvite(i),
                    }),
                    menuItem({
                      label: 'Revoke invite',
                      onClick: () => revokeInvite(i),
                    }),
                  ]
                : []
            }
          />
        ))}
      </Rows>
      {removing && (
        <ConfirmRemoveMemberDialog
          member={removing}
          model={model}
          onClose={() => setRemoving(undefined)}
        />
      )}
    </Rows>
  );
});

const ConfirmRemoveMemberDialog = observer(function _ConfirmRemoveMemberDialog({
  member,
  model,
  onClose,
}: {
  member: MemberModel;
  model: ManageTeamModel;
  onClose(): void;
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<unknown>();

  async function remove() {
    if (loading) return;
    setLoading(true);
    setError(undefined);
    try {
      await model.removeMember(member);
      onClose();
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      title={
        member.isMe ? 'Leave team?' : `Remove ${member.name || member.email}?`
      }
      onClose={loading ? undefined : onClose}
      buttons={
        <>
          <Button
            variant="secondary"
            size="large"
            label="Cancel"
            onClick={onClose}
            disabled={loading} // "Cancel" doesn't load, but you can't cancel while "Remove" is loading
          />
          <Button
            variant="primary"
            background="negative"
            size="large"
            label={member.isMe ? 'Leave' : 'Remove'}
            loading={loading}
            onClick={remove}
          />
        </>
      }
    >
      <TextRegularLight>
        {member.isMe ? 'Once you leave the team you ' : 'They '}
        will no longer have access to projects saved in the team workspace.
      </TextRegularLight>
      <RedErrorText error={error} />
    </Dialog>
  );
});

const ChangeRoleDropdown = observer(function _ChangeRoleDropdown({
  model,
  subject,
}: {
  model: ManageTeamModel;
  subject: MemberModel | InviteModel;
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<unknown>();

  if (model.myRole !== 'admin') {
    return <TextSmallLight>{ROLE_LABEL[subject.role]}</TextSmallLight>;
  }

  const disabledReason =
    (subject.role === 'admin' &&
      subject instanceof MemberModel &&
      model.members.filter((m) => m.role === 'admin').length === 1 &&
      'There must always be one admin in a team') ||
    undefined;

  async function changeTo(role: TeamRole) {
    if (loading || disabledReason != null) return;
    setLoading(true);
    setError(undefined);
    try {
      await subject.changeRole(role);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.changeRoleDropdown}>
      <SelectButton
        menu={[
          menuItem({
            label: 'Admin',
            description: 'Can manage members and billing',
            onClick: () => changeTo('admin'),
            checked: subject.role === 'admin',
          }),
          menuItem({
            label: 'Member',
            description: 'Can view and edit projects',
            onClick: () => changeTo('member'),
            checked: subject.role === 'member',
          }),
        ]}
        disabled={loading || disabledReason != null}
        tooltip={disabledReason}
        fullWidth
      />
      {error != null && (
        <Dialog title="Error changing role" onClose={() => setError(undefined)}>
          <RedErrorText error={error} />
        </Dialog>
      )}
    </div>
  );
});

const ROLE_LABEL = {
  admin: 'Admin',
  member: 'Member',
} as const;

function InviteSentBadge() {
  return <Badge variant="orange" label="Invite sent" />;
}

function MemberOrInviteRow({
  model,
  action,
  menu,
}: {
  model: MemberModel | InviteModel;
  action?: React.ReactNode;
  menu: MenuModel;
}) {
  const member = model instanceof MemberModel ? model : null;
  const name = member?.name;
  const email = model.email;
  return (
    <div className={styles.row}>
      <Inline spacing={16} yalign="center">
        {member && (
          <Avatar
            size="large"
            shape="circle"
            avatar={member.avatar}
            firstName={member.firstName}
            lastName={member.lastName}
            color={member.color}
          />
        )}
        <Rows spacing={0} xalign="start">
          <TextSmallLight>{name || email}</TextSmallLight>
          {name && <TextSmallLight color="grey">{email}</TextSmallLight>}
        </Rows>
        <SpacerGrow />
        {action}
        <div
          className={classNames(
            styles.buttonWrapper,
            (!menu || menu.length === 0) && styles.isHidden,
          )}
        >
          <MenuButton
            variant="tertiary"
            icon={IconOption}
            menu={menu}
            search="hidden"
          />
        </div>
      </Inline>
    </div>
  );
}

const InviteDialog = observer(function _InviteDialog({
  onClose,
  model,
  toastQueue,
}: {
  onClose(): void;
  model: ManageTeamModel;
  toastQueue: ToastQueue;
}) {
  React.useEffect(() => {
    model.fetchInviteLinkInfo();
  }, [model]);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(String(model.invitationLink));
    toastQueue.push({ label: 'Copied to clipboard' });
  };

  const handleResetLink = async () => {
    try {
      await model.resetInviteLink();
      await navigator.clipboard.writeText(String(model.invitationLink));
      toastQueue.push({ label: 'Invite link reset' });
    } catch (error) {
      toastQueue.push({ label: 'Error occured, try again' });
    }
  };

  const handleToggleLinks = async (enabled: boolean) => {
    try {
      await model.toggleInviteLinks(enabled);
    } catch (error) {
      toastQueue.push({ label: 'Error occured, try again' });
    }
  };

  return (
    <Dialog
      title="Invite other members"
      onClose={onClose}
      width="medium"
      fixedWidth={true}
    >
      <InviteTeamForm
        onDone={onClose}
        invite={model.invite}
        inviteLinkInfo={{
          enabled: model.invitationLinkEnabled,
          invitationLink: model.invitationLink,
        }}
        onCopyLink={handleCopyLink}
        onResetLink={handleResetLink}
        onToggleLinks={handleToggleLinks}
      />
    </Dialog>
  );
});

const VacantSeatsBanner = observer(function _VacantSeatsBanner({
  model,
}: {
  model: ManageTeamModel;
}) {
  const n = model.vacantSeats;
  if (n <= 0) {
    return null;
  }
  return (
    <Banner variant="grey3">
      <Inline spacing={8}>
        <TextSmall>
          {n} vacant seat{n === 1 ? '' : 's'}
        </TextSmall>
        <TextSmallLight>
          {n === 1 ? 'It' : 'They'} will be removed from your bill if{' '}
          {n === 1 ? 'it is' : 'they are'} still unused at your next billing
          date.
        </TextSmallLight>
      </Inline>
    </Banner>
  );
});

const ImageNoteBox = ({ text }: { text: string }) => {
  return (
    <div className={styles.imageNoteBox}>
      <TextSmallLight>{text}</TextSmallLight>
    </div>
  );
};
